import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  Renderer2,
  ViewChild,
  OnDestroy,
  EventEmitter,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { VERSION } from 'app/app.constants';
import { AccountService } from 'app/core/auth/account.service';
import { LANGUAGES } from 'app/core/language/language.constants';
import { Account } from 'app/core/models/account.model';
import { SessionStorageService } from 'ngx-webstorage';
import { forkJoin, Subscription, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { AuthServerProvider } from '../../core/auth/auth-jwt.service';
import { AccountUpdate } from '../../core/models/account.model';
import { LazyLoadService } from '../../shared/service/lazy-load.service';
import { AppState } from '../../shared/state/app.state';
import { updateUserLanguage } from '../../shared/state/user.actions';
import { selectCurrentUser, userHasAnyACL, userInfoPecPlus } from '../../shared/state/user.selectors';
import { AppConfiguration } from '../../core/models/app-configuration.model';
import { CompanyLogoService } from '../../shared/service/company-logo.service';
import { StateStorageService } from '../../core/auth/state-storage.service';
import { UserInfo } from 'app/shared/model/user-info.model';
import { AccountACL } from 'app/shared/constants/account-acl.constants';
import { WebMailService } from 'app/shared/service/web_mail.service';
import { WebMail } from 'app/shared/model/web-mail.model';
import { HttpResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { TwoFADevice } from 'app/pages/security/twostepverification/model/device.model';
import { selectCurrent2FaInfos } from 'app/shared/state/twofactor-infos.selectors';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { QuestionPecPlusComponent } from 'app/shared/components/modal/question-pec-plus.component';
import { CompanyService } from 'app/shared/service/company.service';
import { CompanyInfo } from 'app/shared/model/company-info.model';
import { DossierFlowService } from 'app/shared/service/dossier-flow.service';
import { Timeouts } from 'app/shared/constants/timeout.constants';
import { ConversionStrategy, RecognitionStatus, UpgradableStatus } from 'app/shared/constants/user.constants';
import moment from 'moment';

/**
 * Angular component describe navbar and his interaction with application state
 */
@Component({
  selector: 'ap-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['navbar.scss'],
})
export class NavbarComponent implements OnInit, AfterViewInit, OnDestroy {
  highContrastModeEnabled = false;

  isNavbarCollapsed = true;
  languages = LANGUAGES;
  version: string;
  currentLanguage = '';

  isMobileNavVisible = false;

  isPecPlusVisible = 0;
  recognitionStatus: string | null = null;
  webmailUrl!: string;
  isTwoFAStatus = false;
  iconUser = true;
  iconUserBadge = false;
  iconAlert = false;
  iconError = false;
  urlWebmail!: string;
  urlPGA!: string;
  urlDocument: string | null = null;
  mailbox!: string;
  openPecPlus = false;
  openLang = false;
  openMenu = true;
  urlGuide = '';
  UrlPecPlus = '';
  hrefJavascript = '';
  salesChannel: string | null = null;
  dvoExpireDate: string | null = null;
  territoryExpireDate: string | null = null;
  userInfo: UserInfo | null = null;
  webmail: WebMail | null = null;

  authSubscription?: Subscription;
  account: Account | null = null;
  lastClick = '';
  isLegalPersonDeleted = false;
  companyName = '';
  cancelledGuideUrl = '';

  @ViewChild('sidenav')
  sidenav?: ElementRef;

  @ViewChild('pecplus')
  pecplus?: ElementRef;

  hideLanguageChange = false;

  @ViewChild('logo')
  logo?: ElementRef;
  loadingLogo = true;

  destroy$: Subject<boolean> = new Subject<boolean>();
  acls = AccountACL;

  currentCompany?: string;
  @Output() onOpenRoute = new EventEmitter<void>();

  constructor(
    private readonly sessionStorage: SessionStorageService,
    private readonly accountService: AccountService,
    private readonly authServerProvider: AuthServerProvider,
    public readonly companyLogoService: CompanyLogoService,
    public readonly appConfig: AppConfiguration,
    private readonly lazyLoadService: LazyLoadService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly renderer: Renderer2,
    private readonly store: Store<AppState>,
    private readonly storageService: StateStorageService,
    private readonly webMailService: WebMailService,
    private readonly translateService: TranslateService,
    private readonly modalService: NgbModal,
    private readonly companyService: CompanyService,
    private readonly dossierFlowService: DossierFlowService
  ) {
    this.version = VERSION ? (VERSION.toLowerCase().startsWith('v') ? VERSION : 'v' + VERSION) : '';
  }

  /**
   * Component initialization
   * Set current language and register for autentication state change (login/logout or account update)
   */
  ngOnInit(): void {
    this.currentLanguage = this.translateService.currentLang;
    this.currentCompany = this.storageService.getLoggedCompany();
    this.urlGuide = this.appConfig.guideQuestionPecPlus;
    this.cancelledGuideUrl = this.appConfig.cancelledGuideUrl;
    this.hrefJavascript = this.appConfig.hrefJavascript;

    this.store
      .select(selectCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((account: Account | null) => {
        this.account = account;
        if (this.account) {
          this.mailbox = this.account.lastname;
          if (this.account.lang) {
            this.currentLanguage = this.sessionStorage.retrieve('locale') || this.account.lang;
          }

          this.needLoadCrowdin();
          this.needLoadUsabilla();
        }
      });

    this.store
      .select(userHasAnyACL, { acls: AccountACL.PEC_PLUS_VIEW })
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasAcl: boolean) => {
        if (hasAcl) {
          this.getUSerInfo();
        }
      });

    this.getWebmail();
  }

  /**
   * After view initialization try to retrieve company logo.
   * In case of error fallback to arubaPec logo
   */
  ngAfterViewInit(): void {
    /* Mock for  company logo
      this.currentCompany = "pec.selenebs.it";   
    */
    if (this.currentCompany !== 'pec') {
      const redirectPga = this.currentCompany || '';
      this.webMailService.query(redirectPga).subscribe((res: HttpResponse<WebMail>) => {
        if (res.body?.redirectWebMail) {
          this.updateCompanyImage(res.body.logoUrl ? res.body.logoUrl : 'content/images/main_logo.png');
        }
      });
    } else {
      this.updateCompanyImage('content/images/main_logo.png');
    }
    this.loadingLogo = false;
  }

  /**
   * Update company img tag on template
   * @param url Url to new image logo
   */
  updateCompanyImage(url: string | ArrayBuffer): void {
    this.renderer.setStyle(this.logo?.nativeElement, 'background-image', `url(${url})`);
  }

  needLoadUsabilla(): void {
    if (this.appConfig.usabillaEnabled) {
      this.lazyLoadService.lazyLoadExternal('/content/js/usabilla.min.js');
    }
  }

  /**
   * Enable crowdin if required and download required javascript asyncronously
   */
  needLoadCrowdin(): void {
    if (this.appConfig.crowdin.enable && this.activatedRoute.snapshot.queryParams['translationEdit']) {
      // load crowdin
      const languageKey = this.appConfig.crowdin.languageKey;
      if (languageKey) {
        this.translateService.use(languageKey);
        this.currentLanguage = languageKey;
      }

      this.languages = [this.currentLanguage];
      forkJoin([
        this.lazyLoadService.lazyLoadInline("var _jipt = []; _jipt.push(['project', 'pannello-gestione-account-pec']);"),
        this.lazyLoadService.lazyLoadExternal('https://cdn.crowdin.com/jipt/jipt.js'),
      ]).subscribe();
    }
  }

  /**
   * If user select another language, set the language and update all the translation
   * @see JhiLanguageService
   * @param languageKey
   */
  changeLanguage(languageKey: string): void {
    this.translateService.use(languageKey);
    this.sessionStorage.store('locale', languageKey);
    this.store.dispatch(updateUserLanguage({ lang: languageKey }));

    const accountUpdate = new AccountUpdate(this.account?.name || '', this.account?.lastname || '', languageKey);
    this.accountService.save(accountUpdate).subscribe();
  }

  /**
   * Utility method for close any dropdown after click on some link of the navbar
   */
  collapseNavbar(): void {
    this.isNavbarCollapsed = true;
  }

  /**
   * Utility method for hide mobile navbar
   */
  toogleMobileNav(): void {
    setTimeout(() => {
      this.isMobileNavVisible = !this.isMobileNavVisible;
    }, Timeouts.TIMEOUT_100);
  }

  /**
   * Used on mobile device
   * Detect if user touch screen outside navbar and eventually close navbar
   * @param event
   */

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isMobileNavVisible && !this.sidenav?.nativeElement.contains(event.target)) {
      this.toogleMobileNav();
      this.openPecPlus = false;
      this.openLang = false;
    }
  }

  /**
   * Start logout user after click to Logout navbar button
   */
  logout(): void {
    this.collapseNavbar();
    this.storageService.storeUrl(undefined);
    // rimosso per prod this.sessionStorage.clear('scriptAdServerClose');
    this.authServerProvider.logout().subscribe();
  }

  /**
   * Enable/Disable High contrast mode
   */
  switchHighContrast(): void {
    if (this.highContrastModeEnabled) {
      this.renderer.removeClass(document.querySelector('body'), 'high-contrast');
    } else {
      this.renderer.addClass(document.querySelector('body'), 'high-contrast');
    }
    this.highContrastModeEnabled = !this.highContrastModeEnabled;
  }

  sendUsabillaEvent(): void {
    (window as Window & typeof globalThis & { usabilla_live: Function }).usabilla_live('click');
  }

  /**
   * Set navbar icon based on pec plus visibility and recognition status
   */
  setIcon(): void {
    this.iconUser = false;
    this.iconAlert = false;
    this.iconError = false;
    this.iconUserBadge = false;

    if (this.getIsIconUser()) {
      this.iconUser = true;
    }
    if (this.getIsIconUserBadge()) {
      this.iconUserBadge = true;
    }
    if (this.getIsIconAlert()) {
      this.iconAlert = true;
    }
    if (this.getIsIconError()) {
      this.iconAlert = false;
      this.iconError = true;
    }
  }

  private getIsIconUser() {
    return this.isPecPlusVisible === UpgradableStatus.NOT_UPGRADABLE || this.recognitionStatus === RecognitionStatus.IDENTIFICATION_PENDING;
  }

  private getIsIconUserBadge() {
    return this.isPecPlusVisible === UpgradableStatus.JUST_UPGRADED;
  }

  private getIsIconAlert() {
    const statuses = new Set([
      RecognitionStatus.IDENTIFICATION_IN_PROGRESS,
      RecognitionStatus.TERRITORY_PENDING,
      RecognitionStatus.LEGAL_VALIDATION_FAILED,
      RecognitionStatus.OK,
      RecognitionStatus.NOT_IDENTIFIED,
    ]);
    return (
      this.isPecPlusVisible === UpgradableStatus.UPGRADABLE &&
      statuses.has(this.recognitionStatus as RecognitionStatus) &&
      this.verifyAcls(this.acls.DOCAPOSTE_VIEW)
    );
  }

  private getIsIconError() {
    return this.isPecPlusVisible === UpgradableStatus.UPGRADABLE && this.recognitionStatus === RecognitionStatus.DOCUMENTS_REQUIRED;
  }

  /**
   * Subscribe to store user info pec plus value.
   *
   * MOCKS:
   *
   * mock strategy
   * ADD AFTER `this.userInfo = userInfo;`
   *      userInfo.conversionStrategy = 3;
   *
   * mock status
   * ADD AFTER `this.load2fa();`
          this.recognitionStatus = 'IdentificationInProgress';
          this.isPecPlusVisible = 1;
          this.territoryExpireDate = "2023-02-28T13:51:10Z";
          if (this.userInfo.conversionStrategy === 2 && this.recognitionStatus !== 'OK') {
            setTimeout(() => {
              const menuUser = document.getElementById('menuUser');
              menuUser?.classList.add('show');
            }, 2000);
   */
  getUSerInfo(): void {
    this.store
      .select(userInfoPecPlus)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userInfo: UserInfo | null) => {
        if (userInfo) {
          this.userInfo = userInfo;
          const isPasswordExpired = userInfo.passwordExpirationTime?.isSameOrBefore(moment().endOf('day'));
          if (userInfo.changePassword || userInfo.expiredPassword || isPasswordExpired) {
            this.goToGlobalPasswordChange();
          } else {
            if (this.isUserInfoConversionStrategyValid()) {
              this.recognitionStatus = this.userInfo.recognitionStatus;
              this.isPecPlusVisible = this.userInfo.upgradableStatus || UpgradableStatus.NOT_UPGRADABLE;
              this.urlDocument = this.userInfo.recognitionIdsUrl;
              this.salesChannel = this.userInfo.salesChannel;
              this.dvoExpireDate = this.userInfo.dvoExpireDate || '';
              this.territoryExpireDate = this.userInfo.territoryExpireDate || '';
              this.isLegalPersonDeleted = !!this.userInfo.isLegalPersonDeleted;
              if (this.isLegalPersonDeleted) {
                this.companyService.query().subscribe((companyInfo: CompanyInfo) => {
                  this.companyName = companyInfo?.businessName || '';
                });
              }
              this.setIcon();
              this.load2fa();
            }
          }
        }
      });
  }
/**
   * Redirect user to Global password change flow
   */
  private goToGlobalPasswordChange(): void {
    let globalPasswordUri;
    if (this.verifyAcls(this.acls.DOCAPOSTE_VIEW)) {
      globalPasswordUri = this.appConfig.globalChangePasswordUri;
    } else {
      globalPasswordUri = this.appConfig.globalChangePasswordUriDocaposte;
    }
    if (globalPasswordUri) {
      const { protocol, host, pathname } = window.location;
      const currentOrigin = `${protocol}//${host}${pathname}`;
      const myform = document.createElement('form');
      myform.method = 'POST';
      myform.action = globalPasswordUri;
      myform.style.display = 'none';
      myform.append('Content-Type', 'application/x-www-form-urlencoded');

      const redirectUrl = document.createElement('input');
      redirectUrl.type = 'hidden';
      redirectUrl.name = 'redirect_url';
      redirectUrl.value = currentOrigin;
      myform.appendChild(redirectUrl);

      const token = document.createElement('input');
      token.type = 'hidden';
      token.name = 'token';
      token.value = this.authServerProvider.getToken();
      myform.appendChild(token);

      document.body.appendChild(myform);
      myform.submit();
    }
  }
  private isUserInfoConversionStrategyValid() {
    const strategies = new Set([
      ConversionStrategy.ONE,
      ConversionStrategy.TWO,
      ConversionStrategy.THREE,
      ConversionStrategy.FOUR,
      ConversionStrategy.FIVE,
    ]);
    return strategies.has(this.userInfo?.conversionStrategy as ConversionStrategy);
  }

  /**
   * Subscribe to store changes in 2FA information and set 2FA status based on whether 2FA is enabled
   */
  load2fa(): void {
    this.store
      .select(selectCurrent2FaInfos)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: { twoFaEnabled: boolean | null; twoFaDevice: TwoFADevice | null; twoFaEnabledLoaded: boolean }) => {
        if (res.twoFaEnabledLoaded) {
          this.isTwoFAStatus = res.twoFaEnabled ? true : false;
        }
      });
  }

  /**
   * Sets PGA and Webmail urls from app config if current company is pec,
   * otherwise queries the values from web_mail
   */
  getWebmail(): void {
    if (this.currentCompany === 'pec') {
      this.urlPGA = this.appConfig.pgaUrl + 'security/2stepverification';
      this.urlWebmail = this.appConfig.webmailUrl;
    } else {
      const redirectPga = this.currentCompany || '';
      this.webMailService.query(redirectPga).subscribe((res: HttpResponse<WebMail>) => {
        if (res.body?.redirectWebMail) {
          this.urlWebmail = res.body.redirectWebMail;
          this.urlPGA = res.body.redirectPGA || '';
        }
      });
    }
  }

  /**
   * Opens QuestionPecPlusComponent
   */
  openInfoModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(QuestionPecPlusComponent, { size: 'lg' });
    modalRef.result.finally(() => {});
  }

  /**
   * Queries dossier, redirects to ids if response is success,
   * otherwise shows error dialog based on error status, error key and sales channel
   */
  openReadDossier(): void {
    this.dossierFlowService.executeDossierFlow({
      mailbox: this.mailbox,
      urlPGA: this.urlPGA,
      urlWebmail: this.urlWebmail,
      salesChannel: this.salesChannel || '',
    });
  }

  /** Emit event when user navigate to a new sidebar page */
  openRoute(): void {
    this.openPecPlus = false;
    this.onOpenRoute.emit();
  }

  togglePecPlus(event: MouseEvent, back: boolean): void {
    if (this.verifyAcls(this.acls.DOCAPOSTE_VIEW)) {
      this.openPecPlus = !this.openPecPlus;
      if (this.openPecPlus || back) {
        event.stopPropagation();
        event.preventDefault();
      }
    }
  }

  togglelanguage(event: MouseEvent, back: boolean): void {
    this.openLang = !this.openLang;
    this.openPecPlus = false;
    if (this.openLang || back) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
  openCusermerArea(): void {
    window.open(this.appConfig.PECCustomerArea, '_self');
  }
  /**
   * Check a specific ACL for user
   * @param acl the access control list to check
   */
  verifyAcls(acl: AccountACL): boolean {
    return !this.account?.acls.some((authority: AccountACL) => [acl].includes(authority));
  }
  ngOnDestroy(): void {
    if (this.destroy$ && !this.destroy$.closed) {
      this.destroy$.next(true);
      this.destroy$.unsubscribe();
    }
  }
}
